import qs from 'qs';
import { invoker, __, pipe, toPairs, reject, nth, contains, fromPairs, propOr, when, has, prop, map, any, equals, apply, T, both, always } from 'ramda';
import { createEpic, bindActionCreators, mergeProps } from '@ux/fabric';
import { combineEpics } from 'redux-most';
import { LOCATION_CHANGE } from 'connected-react-router';
import { createAction } from 'redux-actions';
import { connect } from 'react-redux';
import { compose, withHandlers } from 'recompose';

var CDN = 'https://tags.tiqcdn.com/utag';
var SENSITIVE_KEYS = ['token', 'tokenId', 'kvp', 'payerId'];

var buildUrl = function buildUrl(env, profile, account) {
  return "".concat(CDN, "/").concat(account, "/").concat(profile, "/").concat(env, "/utag.js");
};

var mount = function mount(fetchScript, filter, global) {
  return function (options) {
    if (options.data) {
      global.utag_data = options.data;
    }

    var url = buildUrl(options.environment, options.profile, options.account);
    filter(global.location.pathname, global.location.search, options.sensitiveKeys);
    return fetchScript(url);
  };
};

var parseQs = invoker(2, 'parse')(__, {
  ignoreQueryPrefix: true
}, qs); // eslint-disable-line max-len

var stringifyQs = invoker(2, 'stringify')(__, {
  addQueryPrefix: true
}, qs); // eslint-disable-line max-len

var filterSensitiveQueryParams = function filterSensitiveQueryParams(global) {
  return function (pathname, search, sensitiveKeys) {
    var query = pipe(parseQs, toPairs, // @ts-ignore
    reject(pipe(nth(0), contains(__, sensitiveKeys || SENSITIVE_KEYS))), fromPairs, stringifyQs)(search);
    global.history.replaceState({}, '', "".concat(pathname).concat(query));
  };
};

var fetchScript = function fetchScript(document) {
  return function (url) {
    return new Promise(function (resolve, reject) {
      var body = document.querySelector('body');
      var script = document.createElement('script');
      script.src = url;
      script.onload = resolve;
      script.onerror = reject;
      script.type = 'text/javascript';
      script.async = true;

      if (body != null) {
        body.appendChild(script);
      }
    });
  };
};

var dispatchEvent = function dispatchEvent(window) {
  return function (data) {
    pipe(propOr({}, 'utag'), when(has('link'), invoker(1, 'link')(data)))(window);
  };
}; // Sends analytics directly through GA

var dispatchGAEvent = function dispatchGAEvent(window, createQueue) {
  var test = function test() {
    // @ts-ignore
    var ga = prop('ga', window);

    if (typeof ga !== 'function') {
      return false;
    }

    if (typeof ga.getAll !== 'function') {
      return false;
    } // @ts-ignore


    var names = pipe(invoker(0, 'getAll'), map(invoker(1, 'get')('name')))(ga);
    return any(equals('tealium_0'), names);
  };

  var action = function action() {
    // @ts-ignore
    var ga = prop('ga', window);

    for (var _len = arguments.length, data = new Array(_len), _key = 0; _key < _len; _key++) {
      data[_key] = arguments[_key];
    }

    ga.apply(void 0, ['tealium_0.send'].concat(data));
  };

  var queue = createQueue(test, action);
  return queue;
};

var createQueue = function createQueue(test, action) {
  var queue = [];
  var flushing = false;
  var tries = 0;

  var flushQueue = function flushQueue() {
    tries = tries + 1;

    if (tries > 20) {
      flushing = false;
      return;
    }

    if (!test()) {
      flushing = true;
      return setTimeout(flushQueue, 1000);
    }

    tries = 0;

    while (queue.length) {
      queue.splice(0).forEach(apply(action));
    }

    flushing = false;
  };

  var addToQueue = function addToQueue() {
    for (var _len = arguments.length, data = new Array(_len), _key = 0; _key < _len; _key++) {
      data[_key] = arguments[_key];
    }

    queue.push(data);

    if (!flushing) {
      flushQueue();
    }
  };

  return addToQueue;
};

var prefix = '@@ux/tagmg/S_';
var TRACK_INTERACTION = "".concat(prefix, "TRACK_INTERACTION");
var trackInteraction = createAction(TRACK_INTERACTION, function (event, source) {
  return {
    event: event,
    source: source
  };
});

var index = /*#__PURE__*/Object.freeze({
  TRACK_INTERACTION: TRACK_INTERACTION,
  trackInteraction: trackInteraction
});

var trackInteractionEpic = function trackInteractionEpic(dispatchEvent) {
  return createEpic({
    signal: TRACK_INTERACTION,
    process: function process(action) {
      var _action$payload = action.payload,
          event = _action$payload.event,
          source = _action$payload.source;
      dispatchEvent({
        event: event,
        source: source
      });
    }
  });
};
var pageViewEpic = function pageViewEpic(dispatchEvent) {
  return function (config) {
    return createEpic({
      signal: LOCATION_CHANGE,
      process: function process(action) {
        var test = config.pageviewTest || T;
        var isFirstRendering = action.payload.isFirstRendering || false;
        var location = action.payload.location;
        var page = location.pathname;
        var shouldDispatch = both(always(isFirstRendering !== true), test)(location);

        if (shouldDispatch) {
          dispatchEvent({
            hitType: 'pageview',
            page: page
          });
        }
      }
    });
  };
};
var _epics = (function (dispatchEvent, dispatchGAEvent) {
  return function () {
    var config = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return combineEpics([trackInteractionEpic(dispatchEvent), pageViewEpic(dispatchGAEvent)(config)]);
  };
});

var composite = (function () {
  var dispatchEvent$1 = dispatchEvent(window);

  var dispatchGAEvent$1 = dispatchGAEvent(window, createQueue);

  var fetchScript$1 = fetchScript(document);

  var filterSensitiveQueryParams$1 = filterSensitiveQueryParams(window); // eslint-disable-line max-len


  var mount$1 = mount(fetchScript$1, filterSensitiveQueryParams$1, window);

  var epic = _epics(dispatchEvent$1, dispatchGAEvent$1);

  return {
    dispatchEvent: dispatchEvent$1,
    dispatchGAEvent: dispatchGAEvent$1,
    mount: mount$1,
    epic: epic
  };
});

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

var mapStateToProps = always({});
var mapDispatchToProps = bindActionCreators({
  trackInteraction: trackInteraction
});

var createHandlers = function createHandlers(config) {
  return _defineProperty({}, config.trigger, function (props) {
    return function () {
      // dispatch gtm event
      props.actions.trackInteraction(config.event, config.source); // call original handler...

      if (typeof props[config.trigger] === 'function') {
        for (var _len = arguments.length, evt = new Array(_len), _key = 0; _key < _len; _key++) {
          evt[_key] = arguments[_key];
        }

        props[config.trigger].apply(null, evt);
      }
    };
  });
};

var trackInteraction$1 = function trackInteraction(options) {
  var config = Object.assign({
    event: 'interaction',
    trigger: 'onClick'
  }, options);
  return compose(connect(mapStateToProps, mapDispatchToProps, mergeProps), withHandlers(createHandlers(config)));
}; // eslint-disable-next-line no-underscore-dangle

var _composite = composite(),
    dispatchEvent$1 = _composite.dispatchEvent,
    dispatchGAEvent$1 = _composite.dispatchGAEvent,
    epic = _composite.epic,
    mount$1 = _composite.mount;

export { dispatchEvent$1 as dispatchEvent, dispatchGAEvent$1 as dispatchGAEvent, epic, mount$1 as mount, index as signals, trackInteraction$1 as trackInteraction };
